/**
 * Simple LRU cache for API responses
 * Stores responses in memory with configurable TTL
 */

import { logger } from './logger';

interface CacheEntry<T> {
    data: T;
    timestamp: number;
    ttl: number;
}

class Cache {
    private cache: Map<string, CacheEntry<any>>;
    private maxSize: number;
    private enabled: boolean;

    constructor(maxSize: number = 100) {
        this.cache = new Map();
        this.maxSize = maxSize;
        this.enabled = process.env.REACT_APP_API_CACHE_ENABLED !== 'false';
    }

    /**
     * Generate cache key from request details
     */
    private generateKey(method: string, url: string, body?: any): string {
        const bodyHash = body ? JSON.stringify(body) : '';
        return `${method}:${url}:${bodyHash}`;
    }

    /**
     * Check if cache entry is still valid
     */
    private isValid(entry: CacheEntry<any>): boolean {
        const age = Date.now() - entry.timestamp;
        return age < entry.ttl;
    }

    /**
     * Get cached response if available and valid
     */
    get<T>(method: string, url: string, body?: any): T | null {
        if (!this.enabled) return null;

        const key = this.generateKey(method, url, body);
        const entry = this.cache.get(key);

        if (!entry) {
            logger.logCacheMiss(key);
            return null;
        }

        if (!this.isValid(entry)) {
            this.cache.delete(key);
            logger.logCacheMiss(key);
            return null;
        }

        logger.logCacheHit(key);
        return entry.data as T;
    }

    /**
     * Store response in cache
     */
    set<T>(method: string, url: string, data: T, ttl: number, body?: any): void {
        if (!this.enabled) return;

        const key = this.generateKey(method, url, body);

        // Implement LRU eviction if cache is full
        if (this.cache.size >= this.maxSize) {
            const firstKey = this.cache.keys().next().value;
            if (firstKey) {
                this.cache.delete(firstKey);
            }
        }

        this.cache.set(key, {
            data,
            timestamp: Date.now(),
            ttl,
        });
    }

    /**
     * Clear cache entries matching a pattern
     */
    clear(pattern?: string): void {
        if (!pattern) {
            this.cache.clear();
            logger.logCacheInvalidation('all');
            return;
        }

        const keysToDelete: string[] = [];
        for (const key of this.cache.keys()) {
            if (key.includes(pattern)) {
                keysToDelete.push(key);
            }
        }

        keysToDelete.forEach(key => this.cache.delete(key));
        logger.logCacheInvalidation(pattern);
    }

    /**
     * Get cache statistics
     */
    getStats() {
        return {
            size: this.cache.size,
            maxSize: this.maxSize,
            enabled: this.enabled,
        };
    }
}

export const cache = new Cache();
